import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Play, Sparkles, Star, Tv } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { resolveUrl } from "../lib/url";

const DEFAULT_HERO = {
  bannerUrl: "https://images.unsplash.com/photo-1645323927863-aa2679b76e5c?w=1920&q=85",
  eyebrow: "Welcome to Kids Mode",
  title: "Stories that spark joy.",
  subtitle: "A safe space full of cartoons, learning shows and family adventures.",
};

export default function Kids() {
  const [movies, setMovies] = useState([]);
  const [series, setSeries] = useState([]);
  const [channels, setChannels] = useState([]);
  const [hero, setHero] = useState(DEFAULT_HERO);
  const { toggleKidsMode } = useAuth();

  useEffect(() => {
    toggleKidsMode(true);
    Promise.all([
      api.get("/movies?kids=true"),
      api.get("/series?kids=true"),
      api.get("/channels?kids=true"),
      api.get("/settings/kids"),
    ]).then(([m, s, c, k]) => {
      setMovies(m.data);
      setSeries(s.data);
      setChannels(c.data);
      const settings = k.data || {};
      setHero({
        bannerUrl: settings.bannerUrl || DEFAULT_HERO.bannerUrl,
        eyebrow: settings.eyebrow || DEFAULT_HERO.eyebrow,
        title: settings.title || DEFAULT_HERO.title,
        subtitle: settings.subtitle || DEFAULT_HERO.subtitle,
      });
    });
  }, [toggleKidsMode]);

  return (
    <div className="kids-mode min-h-screen text-white" data-testid="kids-page">
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#0B1120]/70 border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-4 flex items-center justify-between">
          <Link to="/browse" className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-[#3B82F6] to-[#FBBF24] flex items-center justify-center font-black text-white text-xl" style={{ fontFamily: "Outfit" }}>D</div>
            <span className="text-2xl font-black tracking-tight" style={{ fontFamily: "Outfit" }}>
              Djino<span className="text-[#FBBF24]">Kids</span>
            </span>
          </Link>
          <Link to="/browse" onClick={() => toggleKidsMode(false)} className="bg-white/15 hover:bg-white/25 backdrop-blur-md px-4 py-2 rounded-2xl text-sm font-bold" data-testid="exit-kids-btn">
            Exit Kids
          </Link>
        </div>
      </nav>

      <div className="pt-28 px-6 sm:px-12 max-w-[1600px] mx-auto pb-20">
        {/* Hero */}
        <div className="relative h-[55vh] rounded-3xl overflow-hidden mb-12 bg-gradient-to-br from-[#3B82F6]/30 to-[#FBBF24]/30">
          <img src={resolveUrl(hero.bannerUrl)} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B1120] via-[#0B1120]/40 to-transparent" />
          <div className="absolute inset-0 flex flex-col justify-end p-8 sm:p-12">
            <span className="inline-flex items-center gap-2 text-sm font-bold text-[#FBBF24] mb-3"><Sparkles size={16} /> {hero.eyebrow}</span>
            <h1 className="text-5xl sm:text-7xl font-black mb-4 tracking-tighter" style={{ fontFamily: "Outfit" }}>
              {hero.title}
            </h1>
            <p className="text-lg text-[#cbd5e1] max-w-xl mb-6">
              {hero.subtitle}
            </p>
          </div>
        </div>

        {/* Movies */}
        {movies.length > 0 && (
          <section className="mb-12">
            <h2 className="text-3xl font-black mb-5 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <Star className="text-[#FBBF24] fill-[#FBBF24]" /> Top Movies
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {movies.map((m) => (
                <Link key={m.id} to={`/movie/${m.id}`} className="group bg-white/5 hover:bg-white/10 rounded-3xl overflow-hidden border border-white/10 transition-all hover:scale-105" data-testid={`kids-movie-${m.id}`}>
                  <div className="aspect-[3/4] relative overflow-hidden">
                    <img src={m.posterUrl} alt={m.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition" />
                    <div className="absolute bottom-3 left-3 right-3 opacity-0 group-hover:opacity-100 transition">
                      <div className="bg-[#FBBF24] text-[#0B1120] rounded-full w-12 h-12 flex items-center justify-center font-bold">
                        <Play size={20} className="fill-[#0B1120]" />
                      </div>
                    </div>
                  </div>
                  <div className="p-3">
                    <div className="font-bold text-sm" style={{ fontFamily: "Outfit" }}>{m.title}</div>
                    <div className="text-xs text-[#94a3b8] mt-0.5">{m.duration}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Series */}
        {series.length > 0 && (
          <section className="mb-12">
            <h2 className="text-3xl font-black mb-5 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <Sparkles className="text-[#3B82F6]" /> Cartoons & Series
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {series.map((s) => (
                <Link key={s.id} to={`/series/${s.id}`} className="group bg-white/5 hover:bg-white/10 rounded-3xl overflow-hidden border border-white/10 transition-all hover:scale-105" data-testid={`kids-series-${s.id}`}>
                  <div className="aspect-[3/4] relative overflow-hidden">
                    <img src={s.posterUrl} alt={s.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-3">
                    <div className="font-bold text-sm" style={{ fontFamily: "Outfit" }}>{s.title}</div>
                    <div className="text-xs text-[#94a3b8]">{s.totalSeasons} season{s.totalSeasons > 1 ? "s" : ""}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Channels */}
        {channels.length > 0 && (
          <section>
            <h2 className="text-3xl font-black mb-5 flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <Tv className="text-[#3B82F6]" /> Live Kids Channels
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
              {channels.map((c) => (
                <Link key={c.id} to={`/live/${c.id}`} className="group bg-white/5 hover:bg-white/10 rounded-3xl overflow-hidden border border-white/10 transition" data-testid={`kids-channel-${c.id}`}>
                  <div className="aspect-video relative overflow-hidden">
                    <img src={c.logoUrl} alt={c.channelName} className="w-full h-full object-cover" />
                    <span className="absolute top-2 left-2 live-badge">LIVE</span>
                  </div>
                  <div className="p-3">
                    <div className="font-bold text-sm" style={{ fontFamily: "Outfit" }}>{c.channelName}</div>
                    <div className="text-xs text-[#94a3b8]">{c.currentProgram}</div>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
